import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { getCategoryIcon, getNumberedPointIconColor, getNumberedPointIconShape } from '../helpers/markers';
import { isDefined, isFalsy, shouldDisplayAddPointElement } from '../helpers/utils';

import { ChallengeType } from '../types';
import { PointCardBase } from './point-card-base';
import challengeData from '../helpers/state/challenge-data';
import { getImageUrls } from '../helpers/components/point-card';
import { getValidatedIcon } from '../helpers/icon-loader';
import { makeColorsIntoNumberSvg } from '../helpers/colors';
import topicData from '../helpers/state/topic-data';

@customElement('basic-point-card')
export class BasicPointCard extends PointCardBase {
    static amplitude_callsite: string = 'Basic Point Card';

    @property({ type: Number })
    challenge_points?: number;

    @property({ type: Boolean })
    geo_location_eligible: boolean = false;

    @property({ type: Boolean })
    checked_in: boolean = false;

    @property({ type: Boolean })
    vertical: boolean = false;

    @property({ type: String })
    pillText?: string;

    @state()
    subtext?: string;

    @state()
    is_saved_point: boolean = false;

    @state()
    eventString: string = '';

    private async onCardClick() {
        this.dispatchEvent(new CustomEvent('on-card-click'));
    }

    protected async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('point')) {
            if (!this.point) {
                return;
            }
            this.imageUrls = [];
            this.imageUrls = await getImageUrls(this.point, topicData.topic, topicData.topicKey, PointCardBase.amplitude_callsite);
            if (topicData.topic?.has_dates === true) {
                this.eventString = await this.getEventString() ?? '';
            };
        }
    }

    private get thumbnailIconUrl(): string {
        const categoryIcon = getCategoryIcon(this.category?._id);
        return this.point?.custom_icon_url
            ? getValidatedIcon(this.point.custom_icon_url, categoryIcon)
            : categoryIcon;
    }

    render() {
        return html`
            <div class="mini-point-card" role="button" tabindex="0" aria-label="${this.point?.name ?? 'Location'}" @mouseenter=${this.onHoverMarker} @mouseleave=${this.onExitHoverMarker} @click="${this.onCardClick}" @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void this.onCardClick(); }}}>
                ${this.hasImages ?
                    html`<div class="mini-point-card__image-wrapper">
                        <img @error="${(e: Event) => this.handleImageError(e)}" src=${this.firstImage} class="mini-point-card__image" style="object-fit: ${topicData.topic?.image_display ?? 'cover'}" alt=""/>
                        </div>`
                    : html`<div class="mini-point-card__image-wrapper wrapper-icon">
                        <img src=${this.thumbnailIconUrl} class="mini-point-card__icon" alt=""/>
                        </div>`
                }
                <div class="mini-point-card__content_image">
                    <span class="mini-point-card__category-wrapper">
                        ${topicData.topic?.enable_point_numbering === true && this.point?.number !== undefined
                            ? html`<img src="${makeColorsIntoNumberSvg(getNumberedPointIconColor(topicData.topic, this.point.topic_category_id.$oid), this.point?.number, getNumberedPointIconShape(topicData.topic, this.point.topic_category_id.$oid))}"
                                        class="mini-point-card__point-number"
                                        width="28"
                                        height="28"/>`
                            : ''
                        }
                        ${this.challenge_points !== undefined
                            ? html`<span class="mini-point-card__category-pill">
                                    ${this.challenge_points} ${this.challenge_points === 1 ? 'point' : 'points'}
                                </span>`
                            : ''
                        }
                        ${this.geo_location_eligible && !this.checked_in ?
                            html`<span class="mini-point-card__category-pill">
                                    <mwc-icon style="--mdc-icon-size: 12px;" aria-hidden="true">
                                        near_me
                                    </mwc-icon>
                                    ${challengeData.challenge?.button_text ?? 'Check In'} Now
                                </span>`
                            : ''
                        }
                        ${this.pillText ?
                            html`<span class="mini-point-card__category-pill">
                                    ${this.pillText}
                                </span>`
                            : ''
                        }
                    </span>
                    ${shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.title?.hide_on_point_card)
                        ? html`<span class="mini-point-card__title-text">
                                ${`${this.point?.featured === true ? `${topicData.topic?.featured_name ?? 'Featured'}:` : ''} ${this.point?.name ?? ''}`}
                            </span>`
                        : ''
                    }
                    ${isDefined(this.point?.start) && topicData.topic?.has_dates === true && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.date_time?.hide_on_point_card)
                        ? html`<span class="mini-point-card__address">
                            <mwc-icon class="expanded-point-card__event-icon">
                                      calendar_month
                                  </mwc-icon>
                                ${this.eventString}
                            </span>`
                        : ''
                    }
                    ${this.point?.location.place_id !== 'custom' && this.point?.location.place_id !== 'shape' && isFalsy(topicData.topic?.disable_location_ui) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.location?.hide_on_point_card)
                        ? html`<span class="mini-point-card__address">
                                ${this.subtext ?? this.point?.location.search}
                            </span>`
                        : ''
                    }
                </div>
                ${this.checked_in ? html`<img src="/images/checked_in_big.png" style="width: 20px; height: 20px; margin-top: 4px;" alt="Checked in" />` : ''}
                ${challengeData.isType(ChallengeType.Guide) ? html`
                    ${!this.is_saved_point ? html`
                        <form-button
                            variant="outline"
                            style="margin-top: 4px; margin-right: 4px;"
                            icon="save"
                            size="xs"
                            @click=${(e: Event) => { e.stopPropagation(); this.dispatchEvent(new CustomEvent('on-save-point'));}}
                            >
                            Save
                        </form-button>
                    ` : html`
                        <form-button
                            icon="check"
                            style="margin-top: 4px; margin-right: 4px;"
                            size="xs"
                            @click=${(e: Event) => { e.stopPropagation(); this.dispatchEvent(new CustomEvent('on-save-point'));}}
                            >
                            Saved
                        </form-button>
                    `}
                ` : ''}
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-icon-button-size: 36px;
                --mdc-typography-button-letter-spacing: 0px;
            }

            .mini-point-card {
                word-wrap: break-word;
                border-radius: 4px;
                display: flex;
                height: 80px;
                cursor: pointer;
                overflow-y: auto;
                box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
                gap: 8px;
            }

            .mini-point-card:focus-visible {
                outline: 2px solid var(--main-primary-color, #005fcc);
                outline-offset: 2px;
            }

            .mini-point-card__content_image {
                padding: 6px 16px 6px 8px;
                width: 60%;
                background: var(--theme-white-color);
                overflow: hidden;
                opacity: 0.98;
                flex: 1;
                padding-left: 3px;
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .mini-point-card__content_no_image {
                padding: 12px 16px;
                width: 100%;
                background: white;
                overflow: hidden;
                opacity: 0.98;
            }

            .mini-point-card__image {
                width: 100%;
                height: 100%;
                object-fit: cover;
                opacity: 1;
            }

            .mini-point-card__icon {
                height: 75%;
                width: auto;
                opacity: 1;
                background-color: var(--theme-border-color);
            }

            .mini-point-card__point-number {
                margin-right: 2px;
                z-index: 1;
            }

            .mini-point-card__date {
                color: var(--theme-lighter-text-color);
                display: block;
                margin-bottom: 8px;
                overflow: hidden;
                text-overflow: ellipsis;
                height: 55px;
            }

            .mini-point-card__description {
                color: var(--theme-lighter-text-color);
                display: block;
                margin-bottom: 8px;
                overflow: hidden;
                text-overflow: ellipsis;
                height: 55px;
            }

            .mini-point-card__cover {
                height: 40px;
                background: linear-gradient(to top, white, transparent 100%);
                margin-top: -50px;
                padding: 5px;
                width: 100%;
                margin-left: -5px;
                position: relative;
            }

            .mini-point-card__category-wrapper {
                display: flex;
                gap: 5px;
                white-space: nowrap;
                flex-wrap: wrap;
            }

            .expanded-point-card__address-icon,
            .expanded-point-card__event-icon {
                vertical-align: middle;
                --mdc-icon-size: 14px;
                width: 14px;
            }

            .mini-point-card__category-pill {
                border-radius: 20px;
                font-size: 12px;
                padding: 2px 6px;
                gap: 3px;
                display: flex;
                align-items: center;
                border: 1px solid var(--theme-border-color);
            }

            .mini-point-card__image-wrapper {
                display: flex;
                width: 25%;
                background-color: var(--theme-white-color);
                overflow: hidden;
                align-items: center;
                opacity: 0.98;
            }

            .wrapper-icon {
                background-color: var(--theme-border-color);
                justify-content: center;
            }

            .space-wrapper {
                flex: 1;
                background-color: var(--theme-white-color);
            }

            .mini-point-card__title-text {
                font-weight: 700;
                font-size: 14px;
                color: var(--main-primary-color);
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__address {
                color: var(--theme-lighter-text-color);
                display: block;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__event {
                color: var(--theme-lighter-text-color);
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__event-icon {
                vertical-align: middle;
                --mdc-icon-size: 20px;
                width: 16px;
                padding-right: 4px;
            }

            .mini-point-card__title--featured {
                color: var(--main-featured-color);
                align-self: center;
                padding-right: 4px;
            }

            .mini-point-card__title {
                display: flex;
            }

            .deselect-control {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 1rem;
                gap: 0.25rem;
                cursor: pointer;
                background-color: var(--theme-opacity-background-color);
                color: var(--main-primary-color);
                --mdc-icon-size: 2rem;
                font-size: 14px;
            }

            @media (max-width: 600px) {
                .mini-point-card {
                    height: 100px;
                }

                .mini-point-card__content_image {
                    padding: 6px 16px 6px 8px;
                }
            }
        `;
    }
}